// /marketing/budget — Marketing Budget + CAC dashboard.
//
// Shows annual progress toward the firm's marketing-spend-as-%-of-revenue
// goal (default 15%), broken down by hard vs soft dollars, by activity,
// and by content piece. Plus CAC = annual marketing spend / closed-won
// prospects.
//
// Hard dollars are pulled from the existing P&L (cogs.referral_fees +
// expenses.advertising_promotional). Soft dollars come from the
// marketing_time_log table (hours x hourly rate).

import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/apiFetch';
import { useContent } from '../../context/ContentContext';
import { useFinancialData } from '../../context/FinancialDataContext';
import { useClients } from '../../context/ClientsContext';
import SectionLabel from '../../components/common/SectionLabel';
import { formatCurrency } from '../../utils/formatting';
import { combineBudgetSummary, computeLtvStats } from './budgetMath';

const ACTIVITY_LABELS = {
  discipline_1: 'Discipline 1 — Research',
  discipline_2: 'Discipline 2 — Outline',
  drafting: 'Drafting',
  editing: 'Editing',
  posting: 'Posting',
  // Stage names logged by the production board's time-on-move prompt
  planned: 'Planning',
  scripting: 'Scripting',
  recording: 'Recording',
  graphics: 'Graphics',
  scheduled: 'Scheduling / Publishing',
  strategy: 'Strategy / Planning',
  social: 'Social Media',
  email_newsletter: 'Email / Newsletter',
  podcast: 'Podcast',
  networking: 'Networking / Events',
  seo_aeo: 'SEO / AEO Setup',
  other: 'Other',
  discovery_call: 'Discovery Call (sales)',
  strategy_call: 'Strategy Call (sales)',
};

const NON_CONTENT_ACTIVITIES = [
  'strategy', 'social', 'email_newsletter', 'podcast',
  'networking', 'seo_aeo', 'other',
];

const SALES_ACTIVITY_TYPES = new Set(['discovery_call', 'strategy_call']);

// Lead-source labels — mirrors LEAD_SOURCES from pipelineCalculations.js
// but kept local to avoid an import cycle.
const SOURCE_LABELS = {
  'cross-sell': 'Cross-Sell',
  referral: 'Referral',
  solicitor: 'Solicitor (Zoe)',
  coi: 'COI / Partner',
  event: 'Event',
  website: 'Website',
  podcast: 'Podcast',
  youtube: 'YouTube',
  social: 'Social Media',
  other: 'Other',
  unattributed: 'Unattributed',
};

export default function BudgetView() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Pull per-class P&L from FDC. editableByClass.bwm has BWM-only values
  // derived from the global P&L using historical class share ratios — same
  // source the rest of the dashboard uses, so numbers stay consistent.
  const { editableByClass } = useFinancialData();
  const bwmPL = editableByClass?.bwm;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch('/api/data/marketing/summary')
      .then(r => r.json())
      .then(j => { if (!cancelled) { setSummary(j); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [refreshTick]);

  function refresh() { setRefreshTick(t => t + 1); }

  // Build the combined dataset: server-side soft $ + CAC count, frontend-side
  // BWM revenue + BWM hard $. Shared with the marketing dashboard KPI tiles
  // (budgetMath.js) so both screens always agree.
  const combined = useMemo(() => combineBudgetSummary(summary, bwmPL), [summary, bwmPL]);

  if (loading && !summary) return <div className="text-[12px] text-[#888] p-4">Loading budget summary…</div>;
  if (err) return <div className="text-[12px] text-rose p-4">Failed to load: {err}</div>;
  if (!combined) return <div className="text-[12px] text-[#888] p-4">Loading per-class P&L…</div>;

  return (
    <div className="space-y-4">
      <GoalCard summary={combined} />

      <div className="grid grid-cols-2 gap-4">
        <CurrentMonthCard summary={combined} />
        <CACCard summary={combined} />
      </div>

      <MonthlyBreakdownCard summary={combined} />

      <div className="grid grid-cols-2 gap-4">
        <ByActivityCard summary={combined} />
        <ByContentCard summary={combined} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <WinsByChannelCard summary={combined} />
        <LTVCard summary={combined} />
      </div>

      <LogTimeCard onLogged={refresh} />

      <RecentEntriesCard refreshTick={refreshTick} onChange={refresh} />
    </div>
  );
}

// ─── Annual goal progress ──────────────────────────────────────────
function GoalCard({ summary }) {
  const { annual, goalPercent, year } = summary;
  const goalDollars = Math.round(annual.revenue * goalPercent);
  const pct = annual.pctOfRevenue;
  const pctOfGoal = goalDollars > 0 ? Math.min(annual.totalSpend / goalDollars, 1) : 0;
  const onTrack = pct >= goalPercent;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Marketing Spend Goal — {year}</SectionLabel>
      <div className="text-[10px] text-[#888] mt-0.5">
        BWM only · revenue = subscription + investment management + tax planning · hard $ = referral fees + advertising (pulled from per-class P&L)
      </div>

      <div className="mt-3 flex items-baseline gap-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#888]">Current % of Revenue</div>
          <div className={`text-[40px] font-bold leading-none ${onTrack ? 'text-deep-teal' : 'text-navy'}`}>
            {(pct * 100).toFixed(1)}%
          </div>
          <div className="text-[10px] text-[#888] mt-1">Goal: {(goalPercent * 100).toFixed(0)}%</div>
        </div>
        <div className="flex-1">
          <div className="flex justify-between text-[10px] text-[#888] mb-1">
            <span>Spent: {formatCurrency(annual.totalSpend)}</span>
            <span>Goal: {formatCurrency(goalDollars)}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${onTrack ? 'bg-deep-teal' : 'bg-bullogic-blue'}`}
              style={{ width: `${pctOfGoal * 100}%` }}
            />
          </div>
          <div className="text-[10px] text-[#888] mt-1">
            {annual.goalGap > 0
              ? <>Need <span className="font-bold text-navy">{formatCurrency(annual.goalGap)}</span> more spend to reach {(goalPercent * 100).toFixed(0)}% of revenue ({formatCurrency(annual.revenue)})</>
              : <>Over goal by <span className="font-bold text-deep-teal">{formatCurrency(-annual.goalGap)}</span></>
            }
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-gray-100">
        <Stat label="Hard Dollars" value={formatCurrency(annual.hardDollars)} subtitle="Referral fees + Advertising (from P&L)" />
        <Stat label="Soft Dollars" value={formatCurrency(annual.softDollars)} subtitle="Internal time × hourly rate" />
        <Stat label="Total Spend" value={formatCurrency(annual.totalSpend)} subtitle={`${formatCurrency(annual.revenue)} revenue`} />
      </div>
    </div>
  );
}

// ─── Current month snapshot ────────────────────────────────────────
function CurrentMonthCard({ summary }) {
  const { currentMonth } = summary;
  const monthName = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][currentMonth.index];
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>This Month — {monthName}</SectionLabel>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <Stat label="Hard" value={formatCurrency(currentMonth.hardDollars)} />
        <Stat label="Soft" value={formatCurrency(currentMonth.softDollars)} />
        <Stat label="Total" value={formatCurrency(currentMonth.totalSpend)} accent="text-deep-teal" />
      </div>
    </div>
  );
}

// ─── CAC card (Marketing CAC vs Sales CAC vs Total CAC) ────────────
function CACCard({ summary }) {
  const { cac, year } = summary;
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>CAC — {year}</SectionLabel>
      <div className="text-[10px] text-[#888] mt-0.5">
        Marketing = top-of-funnel (hard $ + content/social/SEO time) · Sales = discovery + strategy call time
      </div>
      {cac.totalCac === null ? (
        <div className="text-[12px] text-[#888] mt-3">
          No closed-won prospects in {year} yet. CAC requires at least one win to compute.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 mt-3">
            <Stat label="Won" value={cac.wonCount} subtitle="Closed-won" />
            <Stat
              label="Marketing CAC"
              value={formatCurrency(cac.marketingCac)}
              subtitle={formatCurrency(cac.marketingSpend) + ' total'}
            />
            <Stat
              label="Sales CAC"
              value={formatCurrency(cac.salesCac)}
              subtitle={formatCurrency(cac.salesSpend) + ' total'}
            />
            <Stat
              label="Total CAC"
              value={formatCurrency(cac.totalCac)}
              accent="text-deep-teal"
              subtitle={formatCurrency(cac.annualSpend) + ' total'}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Monthly breakdown table ───────────────────────────────────────
function MonthlyBreakdownCard({ summary }) {
  const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Monthly Breakdown — {summary.year}</SectionLabel>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="border-b border-gray-200 text-[10px] font-bold uppercase tracking-[0.08em] text-[#888]">
              <th className="text-left py-1 pr-3">Month</th>
              <th className="text-right py-1 px-2">Revenue</th>
              <th className="text-right py-1 px-2">Referral</th>
              <th className="text-right py-1 px-2">Advertising</th>
              <th className="text-right py-1 px-2">Soft $</th>
              <th className="text-right py-1 px-2">Total Spend</th>
              <th className="text-right py-1 pl-2">% of Rev</th>
            </tr>
          </thead>
          <tbody>
            {summary.monthly.map((m, i) => {
              const pct = m.revenue > 0 ? (m.totalSpend / m.revenue) : 0;
              return (
                <tr key={m.mk} className="border-b border-gray-50">
                  <td className="py-1 pr-3 text-navy">{MONTH_LABELS[i]}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{m.revenue ? formatCurrency(m.revenue) : '—'}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{m.referralFees ? formatCurrency(m.referralFees) : '—'}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{m.advertising ? formatCurrency(m.advertising) : '—'}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{m.softDollars ? formatCurrency(m.softDollars) : '—'}</td>
                  <td className="py-1 px-2 text-right tabular-nums font-bold text-navy">{m.totalSpend ? formatCurrency(m.totalSpend) : '—'}</td>
                  <td className="py-1 pl-2 text-right tabular-nums text-[#666]">{pct > 0 ? (pct * 100).toFixed(1) + '%' : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Soft dollars by activity ──────────────────────────────────────
function ByActivityCard({ summary }) {
  const { byActivity } = summary;
  const totalDollars = byActivity.reduce((s, a) => s + a.dollars, 0);

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Soft Dollars by Activity</SectionLabel>
      {byActivity.length === 0 ? (
        <div className="text-[12px] text-[#888] mt-3">No time logged yet this year.</div>
      ) : (
        <div className="space-y-1.5 mt-3">
          {byActivity.map(a => {
            const pct = totalDollars > 0 ? a.dollars / totalDollars : 0;
            return (
              <div key={a.activityType} className="text-[12px]">
                <div className="flex justify-between">
                  <span className="text-navy">{ACTIVITY_LABELS[a.activityType] || a.activityType}</span>
                  <span className="text-[#666] tabular-nums">{a.hours.toFixed(1)}h · {formatCurrency(a.dollars)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-0.5">
                  <div className="h-1.5 rounded-full bg-bullogic-blue" style={{ width: `${pct * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Soft dollars by content piece ─────────────────────────────────
function ByContentCard({ summary }) {
  const { entries: contentEntries } = useContent();
  const titleFor = (id) => contentEntries.find(e => e.id === id)?.title || `(content ID ${id})`;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Top Content by Time Invested</SectionLabel>
      {summary.byContent.length === 0 ? (
        <div className="text-[12px] text-[#888] mt-3">No content-attributed time logged yet.</div>
      ) : (
        <div className="space-y-1.5 mt-3">
          {summary.byContent.map(c => (
            <div key={c.contentLogId} className="flex justify-between text-[12px]">
              <span className="text-navy truncate flex-1 pr-3" title={titleFor(c.contentLogId)}>
                {titleFor(c.contentLogId)}
              </span>
              <span className="text-[#666] tabular-nums shrink-0">
                {c.hours.toFixed(1)}h · {formatCurrency(c.dollars)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Wins by source / acquisition channel ──────────────────────────
//
// Where new clients are actually coming from. Pulled from each
// closed-won prospect's `source` field (referral / blog / podcast /
// solicitor / etc.). The "true CAC per channel" requires per-channel
// spend tracking (not yet built — would mean tagging time entries by
// channel). For now this surfaces wins distribution + win rate by
// channel, which already drives the most important decision: is the
// referral pipeline producing more than the content pipeline?
function WinsByChannelCard({ summary }) {
  const { wonBySource, year, cac } = summary;
  const total = cac.wonCount;

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Wins by Channel — {year}</SectionLabel>
      <div className="text-[10px] text-[#888] mt-0.5">
        Where new BWM clients came from this year. Per-channel CAC requires
        channel-tagged spend (Phase 2 — for now use the wins distribution
        to spot which channels deserve more investment.)
      </div>

      {total === 0 ? (
        <div className="text-[12px] text-[#888] mt-3">
          No closed-won prospects in {year} yet.
        </div>
      ) : (
        <div className="space-y-1.5 mt-3">
          {wonBySource.map(s => (
            <div key={s.source} className="text-[12px]">
              <div className="flex justify-between mb-0.5">
                <span className="text-navy">
                  {SOURCE_LABELS[s.source] || s.source}
                </span>
                <span className="text-[#666] tabular-nums">
                  {s.count} {s.count === 1 ? 'win' : 'wins'} · {Math.round(s.pct * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-bullogic-blue" style={{ width: `${s.pct * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LTV + LTV:CAC ratio ───────────────────────────────────────────
//
// Math lives in budgetMath.js (shared with the dashboard CAC tile).
// LTV:CAC ratio = portfolio avg LTV / total CAC. Industry benchmarks:
//   3:1 = healthy
//   1:1 = breakeven (over-spending)
//   5:1+ = under-investing in growth (counter-intuitive but true)
function LTVCard({ summary }) {
  const { clients } = useClients();

  const ltv = useMemo(
    () => computeLtvStats(clients, summary.cac.totalCac),
    [clients, summary.cac.totalCac]
  );

  // Health color for the LTV:CAC ratio
  const ratioColor =
    ltv.ratio === null            ? 'text-[#999]'   :
    ltv.ratio >= 3 && ltv.ratio < 5 ? 'text-deep-teal' :  // healthy
    ltv.ratio >= 5                 ? 'text-bullogic-blue' :  // under-investing
    ltv.ratio < 1                  ? 'text-rose'      :  // losing money
                                     'text-amber-600';   // 1-3 range, breakeven-ish

  const ratioLabel =
    ltv.ratio === null   ? 'No CAC yet'         :
    ltv.ratio >= 5       ? 'Under-investing'    :
    ltv.ratio >= 3       ? 'Healthy (3-5x)'     :
    ltv.ratio >= 1       ? 'Marginal (rebuild)' :
                           'Losing money';

  const TIER_LABELS = {
    'guided-wealth': 'Guided Wealth',
    'total-wealth': 'Total Wealth',
    'tax-plan': 'Tax Plan',
    'investment-only': 'Investment Only',
    unknown: 'Unknown tier',
  };

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>LTV &amp; LTV:CAC Ratio</SectionLabel>
      <div className="text-[10px] text-[#888] mt-0.5">
        Active BWM clients · {ltv.retentionYears}-year retention assumption (will switch to actual avg tenure once we have ≥10 churned clients)
      </div>

      <div className="mt-3 flex items-baseline gap-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#888]">Avg LTV</div>
          <div className="text-[28px] font-bold text-navy leading-tight">{formatCurrency(ltv.avgLtv)}</div>
          <div className="text-[10px] text-[#888] mt-0.5">{ltv.activeCount} active clients</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#888]">LTV:CAC</div>
          <div className={`text-[28px] font-bold leading-tight ${ratioColor}`}>
            {ltv.ratio === null ? '—' : `${ltv.ratio.toFixed(1)}x`}
          </div>
          <div className={`text-[10px] mt-0.5 ${ratioColor}`}>{ratioLabel}</div>
        </div>
      </div>

      {ltv.byTier.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#999] mb-2">Avg LTV by Tier</div>
          <div className="space-y-1">
            {ltv.byTier.map(t => (
              <div key={t.tier} className="flex justify-between text-[12px]">
                <span className="text-navy">{TIER_LABELS[t.tier] || t.tier}</span>
                <span className="text-[#666] tabular-nums">
                  {t.count} {t.count === 1 ? 'client' : 'clients'} · {formatCurrency(t.avgLtv)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Standalone Log Time form (for non-content activities) ─────────
function LogTimeCard({ onLogged }) {
  const [hours, setHours] = useState('');
  const [activityType, setActivityType] = useState('strategy');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const h = Number(hours);
      if (!Number.isFinite(h) || h <= 0) throw new Error('Hours must be a positive number');
      const res = await apiFetch('/api/data/marketing/time-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activityType,
          hours: h,
          notes: notes || null,
          contentLogId: null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setHours('');
      setNotes('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1500);
      onLogged?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Log Time (non-content activity)</SectionLabel>
      <p className="text-[11px] text-[#888] mt-1 mb-3">
        For content-related time (Discipline 1, Drafting, etc.), log it from the Content Log row instead.
      </p>
      <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Hours</label>
          <input
            type="number"
            step="0.25"
            min="0"
            value={hours}
            onChange={e => setHours(e.target.value)}
            placeholder="2.0"
            className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
            required
          />
        </div>
        <div className="col-span-4">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Activity</label>
          <select
            value={activityType}
            onChange={e => setActivityType(e.target.value)}
            className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
          >
            {NON_CONTENT_ACTIVITIES.map(a => (
              <option key={a} value={a}>{ACTIVITY_LABELS[a]}</option>
            ))}
          </select>
        </div>
        <div className="col-span-5">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Notes (optional)</label>
          <input
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="What you worked on"
            className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
          />
        </div>
        <div className="col-span-12 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className={`text-[11px] font-bold px-3 py-1.5 rounded-[3px] ${
              saving
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
            }`}
          >
            {saving ? 'Logging…' : 'Log Time'}
          </button>
          {success && <span className="text-[11px] text-deep-teal">Logged ✓</span>}
          {err && <span className="text-[11px] text-rose">{err}</span>}
        </div>
      </form>
    </div>
  );
}

// ─── Recent time entries (last 25, with delete) ────────────────────
function RecentEntriesCard({ refreshTick, onChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { entries: contentEntries } = useContent();
  const titleFor = (id) => contentEntries.find(e => e.id === id)?.title || null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch(`/api/data/marketing/time-log?year=${new Date().getFullYear()}`)
      .then(r => r.json())
      .then(j => { if (!cancelled) { setEntries(j.entries || []); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshTick]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      const r = await apiFetch(`/api/data/marketing/time-log/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      onChange?.();
    } catch (e) { alert(e.message); }
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <SectionLabel>Recent Time Entries</SectionLabel>
      {loading ? (
        <div className="text-[11px] text-[#888] mt-3">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-[11px] text-[#888] mt-3">No entries this year yet.</div>
      ) : (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-gray-200 text-[10px] font-bold uppercase tracking-[0.08em] text-[#888]">
                <th className="text-left py-1 pr-3">Date</th>
                <th className="text-left py-1 pr-3">Activity</th>
                <th className="text-left py-1 pr-3">Content</th>
                <th className="text-right py-1 px-2">Hours</th>
                <th className="text-right py-1 px-2">Value</th>
                <th className="text-left py-1 pl-3">Notes</th>
                <th className="py-1 pl-2"></th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 25).map(e => (
                <tr key={e.id} className="border-b border-gray-50">
                  <td className="py-1 pr-3 text-[#666]">{e.entryDate}</td>
                  <td className="py-1 pr-3 text-navy">{ACTIVITY_LABELS[e.activityType] || e.activityType}</td>
                  <td className="py-1 pr-3 text-[#666] truncate max-w-[200px]">
                    {e.contentLogId ? (titleFor(e.contentLogId) || `#${e.contentLogId}`) : '—'}
                  </td>
                  <td className="py-1 px-2 text-right tabular-nums">{e.hours.toFixed(2)}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{formatCurrency(e.dollarValueCents / 100)}</td>
                  <td className="py-1 pl-3 text-[#888] truncate max-w-[250px]">{e.notes || '—'}</td>
                  <td className="py-1 pl-2 text-right">
                    <button onClick={() => handleDelete(e.id)} className="text-[10px] text-rose hover:underline">delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Shared little stat cell ───────────────────────────────────────
function Stat({ label, value, subtitle, accent = 'text-navy' }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#888]">{label}</div>
      <div className={`text-[24px] font-bold leading-tight ${accent}`}>{value}</div>
      {subtitle && <div className="text-[10px] text-[#888] mt-0.5">{subtitle}</div>}
    </div>
  );
}
